import type { Ref } from 'vue';
import type {
  IPhieuXuatKhoSelectedKhoSummary,
  PhieuXuatKhoTableFooterMeta,
  PhieuXuatKhoTableRow,
} from '../types';
import type { PhieuXuatKhoTableColumnId } from './usePhieuXuatKhoTableColumns';
import {
  calcThanhTien,
  calcThanhTienTotal,
} from './phieuXuatKhoCalculations';
import { summaryLineToTableRow } from './normalizeTableRow';

export interface FooterColumnIndexes {
  donGiaColumnIndex: number;
  labelColumnIndex: number;
  scrollableColspan: number;
  valueColumnIndex: number;
}

export function usePhieuXuatKhoTableFooter(
  footerIndexes: Ref<FooterColumnIndexes>
) {
  const appendSummaryFooterRows = (
    lines: PhieuXuatKhoTableRow[],
    meta: PhieuXuatKhoTableFooterMeta
  ): PhieuXuatKhoTableRow[] => {
    return [
      ...lines,
      {
        _footerType: 'total',
        tong_tien_bang_chu: meta.tong_tien_bang_chu,
        vat_tu_da_lay: meta.vat_tu_da_lay,
      },
      {
        _footerType: 'text',
        tong_tien_bang_chu: meta.tong_tien_bang_chu,
        vat_tu_da_lay: meta.vat_tu_da_lay,
      },
    ];
  };

  const buildGroupTableData = (
    group: IPhieuXuatKhoSelectedKhoSummary
  ): PhieuXuatKhoTableRow[] => {
    const dataRows: PhieuXuatKhoTableRow[] =
      group.vat_tu_da_lay.map(summaryLineToTableRow);

    return appendSummaryFooterRows(dataRows, {
      tong_tien_bang_chu: group.tong_tien_bang_chu,
      vat_tu_da_lay: group.vat_tu_da_lay,
    });
  };

  const footerSpanMethod = ({
    row,
    column,
    columnIndex,
  }: {
    row: PhieuXuatKhoTableRow;
    column: { property?: string };
    columnIndex: number;
  }) => {
    if (!row._footerType) {
      return;
    }

    const {
      donGiaColumnIndex,
      labelColumnIndex,
      scrollableColspan,
      valueColumnIndex,
    } = footerIndexes.value;

    if (labelColumnIndex < 0 || valueColumnIndex < 0) {
      return;
    }

    const columnId = column.property as PhieuXuatKhoTableColumnId | undefined;

    if (columnId) {
      if (columnId === 'stt') {
        return {
          rowspan: 1,
          colspan: scrollableColspan > 0 ? scrollableColspan : 1,
        };
      }

      if (columnId === 'so_luong_xuat') {
        const colspan =
          donGiaColumnIndex >= labelColumnIndex
            ? donGiaColumnIndex - labelColumnIndex + 1
            : 2;

        return { rowspan: 1, colspan };
      }

      if (columnId === 'don_gia') {
        return { rowspan: 0, colspan: 0 };
      }

      if (columnId === 'thanh_tien') {
        return { rowspan: 1, colspan: 1 };
      }

      return { rowspan: 0, colspan: 0 };
    }

    if (columnIndex === 0) {
      return {
        rowspan: 1,
        colspan: scrollableColspan > 0 ? scrollableColspan : 1,
      };
    }

    if (columnIndex > 0 && columnIndex < labelColumnIndex) {
      return { rowspan: 0, colspan: 0 };
    }

    if (columnIndex === labelColumnIndex) {
      const colspan =
        donGiaColumnIndex >= labelColumnIndex
          ? donGiaColumnIndex - labelColumnIndex + 1
          : 2;

      return { rowspan: 1, colspan };
    }

    if (columnIndex > labelColumnIndex && columnIndex <= donGiaColumnIndex) {
      return { rowspan: 0, colspan: 0 };
    }

    if (columnIndex === valueColumnIndex) {
      return { rowspan: 1, colspan: 1 };
    }

    return { rowspan: 0, colspan: 0 };
  };

  const footerRowClassName = ({ row }: { row: PhieuXuatKhoTableRow }) => {
    return row._footerType ? 'phieu-xuat-kho-vat-tu-table__footer-row' : '';
  };

  return {
    appendSummaryFooterRows,
    buildGroupTableData,
    calcThanhTien,
    calcThanhTienTotal,
    footerRowClassName,
    footerSpanMethod,
  };
}
